import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminTripDetail, sendAdminWhatsAppMessage } from '../services/api/admin';
import type { AdminTripDetail } from '../types/admin';
import {
  paymentOpsByPrimaryBookingUrl,
  paymentOpsByProviderIntentUrl,
  paymentOpsByTripGroupUrl,
  providerEventsByReferenceUrl,
} from '../utils/adminPaymentOpsLinks';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const STATUS_CLASS_MAP: Record<string, string> = {
  COMPLETED: 'admin-pill-success',
  DELIVERED: 'admin-pill-success',
  ACCEPTED: 'admin-pill-active',
  ON_ROUTE_TO_PICKUP: 'admin-pill-active',
  ENROUTE: 'admin-pill-active',
  PICKED_UP: 'admin-pill-active',
  LOADING: 'admin-pill-active',
  UNLOADING: 'admin-pill-active',
  CANCELLED: 'admin-pill-danger',
  CANCELLED_BY_SYSTEM: 'admin-pill-danger',
  REJECTED: 'admin-pill-danger',
  EXPIRED: 'admin-pill-warning',
  PENDING: 'admin-pill-warning',
  ASSIGNED: 'admin-pill-warning',
};

function formatStatusLabel(status: string | null | undefined): string {
  return (status || 'UNKNOWN').replaceAll('_', ' ');
}

function formatDuration(value: number | null | undefined): string {
  if (!value && value !== 0) return 'Not available';
  const hours = Math.floor(value / 3600);
  const minutes = Math.round((value % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes} min`;
}

function formatTimelineMeta(meta: Record<string, unknown> | null | undefined): string {
  if (!meta || !Object.keys(meta).length) return 'No extra metadata';
  return Object.entries(meta)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join('\n');
}

function isWhatsAppAssisted(detail: AdminTripDetail): boolean {
  return (
    detail.bookingSource === 'WHATSAPP_ASSISTED_BOOKING' ||
    Boolean(detail.assistedDispatchReason?.toLowerCase().includes('whatsapp')) ||
    detail.bookingRows.some((row) => Boolean(row.assistedDispatchReason?.toLowerCase().includes('whatsapp')))
  );
}

function needsAdminDispatch(detail: AdminTripDetail): boolean {
  return Boolean(
    detail.adminDispatchRequired ||
      (isWhatsAppAssisted(detail) && !detail.activeDriver?.id && detail.bookingRows.some((row) => !row.driverId)),
  );
}

const AdminTripDetailPage: React.FC = () => {
  const { tripId = '' } = useParams<{ tripId: string }>();
  const [detail, setDetail] = useState<AdminTripDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [messageNotice, setMessageNotice] = useState('');
  const [messageError, setMessageError] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!tripId) {
        setError('Trip ID is missing.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await fetchAdminTripDetail(tripId);
        if (!mounted) return;
        setDetail(response);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load the trip detail.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [tripId]);

  const orderedTimeline = useMemo(() => {
    return [...(detail?.timeline ?? [])].sort((left, right) => {
      const leftMs = left.at ? new Date(left.at).getTime() : 0;
      const rightMs = right.at ? new Date(right.at).getTime() : 0;
      return leftMs - rightMs;
    });
  }, [detail?.timeline]);

  useEffect(() => {
    if (!detail || !isWhatsAppAssisted(detail)) return;
    setMessageBody(
      [
        `Hi ${detail.requester?.name || 'there'}, this is ShiftWagon support.`,
        `We are checking driver availability for booking ${detail.representativeBookingId || detail.tripId}.`,
        'We will update you as soon as dispatch is confirmed.',
      ].join(' '),
    );
  }, [detail]);

  const submitWhatsAppMessage = async () => {
    if (!detail?.requester?.phone) {
      setMessageError('This trip has no customer WhatsApp number.');
      return;
    }

    if (!messageBody.trim()) {
      setMessageError('Enter a message before sending.');
      return;
    }

    setMessageSending(true);
    setMessageError('');
    setMessageNotice('');
    try {
      await sendAdminWhatsAppMessage({
        to: detail.requester.phone,
        body: messageBody.trim(),
      });
      setMessageNotice('WhatsApp message queued for delivery.');
    } catch (sendError: any) {
      setMessageError(sendError.message || 'Could not send the WhatsApp message.');
    } finally {
      setMessageSending(false);
    }
  };

  return (
    <AdminShell
      badge="Trip Detail"
      copy="Follow the full operational picture for one trip, including participants, money, timeline, and underlying booking rows."
      title={
        detail?.representativeBookingId
          ? `Trip ${detail.representativeBookingId}`
          : detail?.tripId
            ? `Trip ${detail.tripId}`
            : 'Trip detail'
      }
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      {loading ? (
        <div className="admin-panel">
          <div className="register-loader" />
        </div>
      ) : detail ? (
        <>
          <p className="admin-muted-note" style={{ marginBottom: '18px' }}>
            Trip ID: {detail.tripId}
          </p>
          <div className="admin-panel" style={{ marginBottom: '18px' }}>
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Trip overview</h2>
                <p className="admin-panel-copy">The backend already resolved the effective current status for this trip. The booking rows below are the underlying audit trail.</p>
              </div>
              <div className="admin-inline-actions">
                <span className={`admin-pill ${STATUS_CLASS_MAP[detail.status || ''] || ''}`}>
                  {formatStatusLabel(detail.status)}
                </span>
                {isWhatsAppAssisted(detail) ? (
                  <span className="admin-pill admin-pill-whatsapp">WhatsApp</span>
                ) : null}
                {needsAdminDispatch(detail) ? (
                  <span className="admin-pill admin-pill-warning">Admin dispatch required</span>
                ) : null}
                <Link className="register-secondary-button" to="/admin/trips">
                  Back to trips
                </Link>
              </div>
            </div>
            <div className="admin-detail-grid">
              <div className="admin-detail-card"><span>Trip ID</span><strong>{detail.tripId}</strong></div>
              <div className="admin-detail-card"><span>Booking ref</span><strong>{detail.representativeBookingId || 'Not available'}</strong></div>
              <div className="admin-detail-card"><span>Flow</span><strong>{detail.flow || 'Not available'}</strong></div>
              <div className="admin-detail-card"><span>Vehicle type</span><strong>{detail.vehicleType || 'Not available'}</strong></div>
              <div className="admin-detail-card"><span>Created at</span><strong>{formatDateTime(detail.createdAt)}</strong></div>
              <div className="admin-detail-card"><span>Scheduled at</span><strong>{formatDateTime(detail.scheduledAt)}</strong></div>
            </div>
          </div>

          <div className="admin-section-grid">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Pickup and dropoff</h2>
                  <p className="admin-panel-copy">Address and coordinate snapshots captured for this trip.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Pickup</span>
                  <strong>{detail.pickup?.line1 || 'Not available'}</strong>
                  <small className="admin-muted-note">{detail.pickup?.lat ?? '-'}, {detail.pickup?.lng ?? '-'}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Dropoff</span>
                  <strong>{detail.dropoff?.line1 || 'Not available'}</strong>
                  <small className="admin-muted-note">{detail.dropoff?.lat ?? '-'}, {detail.dropoff?.lng ?? '-'}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Pickup contact</span>
                  <strong>{detail.pickupContact?.name || detail.pickupContact?.mode || 'Not available'}</strong>
                  <small className="admin-muted-note">{detail.pickupContact?.phone || 'No phone supplied'}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Dropoff contact</span>
                  <strong>{detail.dropoffContact?.name || detail.dropoffContact?.mode || 'Not available'}</strong>
                  <small className="admin-muted-note">{detail.dropoffContact?.phone || 'No phone supplied'}</small>
                </div>
              </div>
            </section>

            {isWhatsAppAssisted(detail) ? (
              <section className="admin-panel">
                <div className="admin-panel-header">
                  <div>
                    <h2 className="admin-panel-title">WhatsApp support</h2>
                    <p className="admin-panel-copy">Send customer updates through the backend WhatsApp channel.</p>
                  </div>
                  {detail.requester?.phone ? (
                    <span className="admin-pill admin-pill-whatsapp">{detail.requester.phone}</span>
                  ) : null}
                </div>
                {messageNotice ? <div className="admin-message-alert">{messageNotice}</div> : null}
                {messageError ? <div className="register-inline-error">{messageError}</div> : null}
                <label className="admin-modal-field">
                  <span>Message</span>
                  <textarea
                    className="register-input"
                    maxLength={4096}
                    onChange={(event) => setMessageBody(event.target.value)}
                    value={messageBody}
                  />
                </label>
                <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="register-secondary-button"
                    disabled={messageSending || !detail.requester?.phone}
                    onClick={submitWhatsAppMessage}
                    type="button"
                  >
                    {messageSending ? 'Sending...' : 'Message customer on WhatsApp'}
                  </button>
                </div>
              </section>
            ) : null}

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Participants</h2>
                  <p className="admin-panel-copy">Requester and currently active driver, when available.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Requester</span>
                  <strong>{detail.requester?.name || detail.requester?.id || 'Not available'}</strong>
                  <small className="admin-muted-note">{detail.requester?.email || 'No email supplied'}</small>
                  <small className="admin-muted-note">{detail.requester?.phone || 'No phone supplied'}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Active driver</span>
                  <strong>{detail.activeDriver?.name || detail.activeDriver?.id || 'Not assigned yet'}</strong>
                  <small className="admin-muted-note">{detail.activeDriver?.email || 'No email supplied'}</small>
                  <small className="admin-muted-note">{detail.activeDriver?.phone || 'No phone supplied'}</small>
                </div>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Route and timing</h2>
                  <p className="admin-panel-copy">Distance and duration snapshots from the main route and the route to pickup.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Main route</span>
                  <strong>{detail.route?.distanceKm ?? 'Not available'} km</strong>
                  <small className="admin-muted-note">{formatDuration(detail.route?.durationSec)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Route to pickup</span>
                  <strong>{detail.routeToPickup?.distanceKm ?? 'Not available'} km</strong>
                  <small className="admin-muted-note">{formatDuration(detail.routeToPickup?.durationSec)}</small>
                </div>
                <div className="admin-detail-card admin-detail-card-span-2">
                  <span>Load notes</span>
                  <strong>{detail.loadNotes || 'No load notes supplied'}</strong>
                </div>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Financials</h2>
                  <p className="admin-panel-copy">Final fare, estimate, commission, payout, and payment metadata.</p>
                </div>
                <div className="admin-inline-actions">
                  {detail.tripGroupId ? (
                    <Link className="admin-payment-action-button" to={paymentOpsByTripGroupUrl(detail.tripGroupId)}>
                      Payment Ops: trip
                    </Link>
                  ) : null}
                  {detail.paymentIntentPublicId ? (
                    <Link className="admin-payment-action-button" to={`/admin/payment-ops/${encodeURIComponent(detail.paymentIntentPublicId)}`}>
                      Declare collection
                    </Link>
                  ) : null}
                  {detail.primaryBookingId || detail.representativeBookingId ? (
                    <Link
                      className="admin-payment-action-button"
                      to={paymentOpsByPrimaryBookingUrl(detail.primaryBookingId || detail.representativeBookingId)}
                    >
                      Payment Ops: booking
                    </Link>
                  ) : null}
                  {detail.providerIntentId || detail.paymentRef ? (
                    <Link
                      className="admin-payment-action-button"
                      to={paymentOpsByProviderIntentUrl(detail.providerIntentId || detail.paymentRef)}
                    >
                      Payment Ops: ref
                    </Link>
                  ) : null}
                  {detail.paymentProvider && (detail.providerIntentId || detail.paymentRef) ? (
                    <Link
                      className="admin-payment-action-button"
                      to={providerEventsByReferenceUrl(detail.paymentProvider, detail.providerIntentId || detail.paymentRef)}
                    >
                      Provider events
                    </Link>
                  ) : null}
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card"><span>Estimated fare</span><strong>{formatCurrency(detail.estimatedFare)}</strong></div>
                <div className="admin-detail-card"><span>Final fare</span><strong>{formatCurrency(detail.fareAmount)}</strong></div>
                <div className="admin-detail-card"><span>Commission</span><strong>{formatCurrency(detail.commissionAmount)}</strong></div>
                <div className="admin-detail-card"><span>Driver payout</span><strong>{formatCurrency(detail.driverPayout)}</strong></div>
                <div className="admin-detail-card"><span>Payment status</span><strong>{detail.paymentStatus || 'Not available'}</strong></div>
                <div className="admin-detail-card"><span>Payment ref</span><strong>{detail.paymentRef || 'Not available'}</strong></div>
                <div className="admin-detail-card"><span>Payment provider</span><strong>{detail.paymentProvider || 'Not available'}</strong></div>
                <div className="admin-detail-card">
                  <span>Offline collection</span>
                  <strong>{detail.collectionSource ? detail.collectionSource.replaceAll('_', ' ') : 'Not active'}</strong>
                  <small>Status: {detail.manualCollectionStatus ? detail.manualCollectionStatus.replaceAll('_', ' ') : 'Not available'}</small>
                  <small>Authorized: {formatDateTime(detail.manualCollectionAuthorizedAt)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Agreed amount</span>
                  <strong>{formatCurrency(detail.agreedAmountOverride)}</strong>
                  <small>Collected: {formatCurrency(detail.manuallyCollectedAmount)}</small>
                  <small>Outstanding: {formatCurrency(detail.outstandingAmount)}</small>
                </div>
                <div className="admin-detail-card"><span>Trip group</span><strong>{detail.tripGroupId || 'Not available'}</strong></div>
                <div className="admin-detail-card"><span>Commission plan</span><strong>{detail.commissionPlanCode || 'Not available'}</strong></div>
                <div className="admin-detail-card"><span>Commission rate</span><strong>{detail.commissionRateBps ? `${detail.commissionRateBps} bps` : 'Not available'}</strong></div>
                <div className="admin-detail-card">
                  <span>Booking source</span>
                  <strong>{isWhatsAppAssisted(detail) ? 'WhatsApp' : detail.bookingSource || 'Standard'}</strong>
                  <small>{detail.driverCandidateCount ?? 'No'} driver candidates recorded</small>
                </div>
                <div className="admin-detail-card">
                  <span>Dispatch note</span>
                  <strong>{detail.assistedDispatchReason || 'No assisted dispatch note'}</strong>
                </div>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Timeline</h2>
                  <p className="admin-panel-copy">Chronological backend events only. The frontend does not invent or infer missing statuses.</p>
                </div>
              </div>
              <div className="admin-list">
                {orderedTimeline.map((event, index) => (
                  <div className="admin-list-card" key={`${event.type}-${event.at}-${index}`}>
                    <strong>{formatStatusLabel(event.type)}</strong>
                    <small>{formatDateTime(event.at)}</small>
                    <pre className="admin-json-block">{formatTimelineMeta(event.meta)}</pre>
                  </div>
                ))}
                {!orderedTimeline.length ? <p className="admin-empty">No timeline events are available for this trip.</p> : null}
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Proof of delivery and cancellation</h2>
                  <p className="admin-panel-copy">Null-safe operational metadata for closing or failed trip flows.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Signed by</span>
                  <strong>{detail.proofOfDelivery?.signedBy || 'Not available'}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>POD photo</span>
                  <strong>{detail.proofOfDelivery?.photoUrl ? 'Available' : 'Not available'}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Cancelled by</span>
                  <strong>{detail.cancelledBy || 'Not cancelled'}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Cancelled at</span>
                  <strong>{formatDateTime(detail.cancelledAt)}</strong>
                </div>
                <div className="admin-detail-card admin-detail-card-span-2">
                  <span>Cancel reason</span>
                  <strong>{detail.cancelReason || 'No cancellation reason recorded'}</strong>
                </div>
              </div>
            </section>
          </div>

          <section className="admin-panel" style={{ marginTop: '18px' }}>
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Underlying booking rows</h2>
                <p className="admin-panel-copy">One row per booking record under this trip, useful for assignment fanout and audit review.</p>
              </div>
            </div>
            <div className="admin-table admin-table-booking-rows">
              <div className="admin-table-head admin-table-head-booking-rows">
                <span>Booking</span>
                <span>Driver</span>
                <span>Status</span>
                <span>Sent</span>
                <span>Viewed</span>
                <span>Accepted</span>
                <span>Expired</span>
                <span>Cancelled</span>
                <span>Completed</span>
              </div>
              {detail.bookingRows.map((row, index) => (
                <div className="admin-table-row admin-table-row-booking-rows" key={`${row.bookingId}-${index}`}>
                  <span>
                    <strong>{row.bookingId || 'No booking ID'}</strong>
                    {row.bookingId ? (
                      <Link className="admin-row-link" to={paymentOpsByPrimaryBookingUrl(row.bookingId)}>
                        Payment Ops
                      </Link>
                    ) : null}
                  </span>
                  <span>
                    <strong>{row.driverName || row.driverId || 'No driver'}</strong>
                    <small>{row.driverEmail || 'No driver email'}</small>
                    <small>{row.driverPhone || 'No driver phone'}</small>
                    {row.assistedDispatchReason ? <small>{row.assistedDispatchReason}</small> : null}
                  </span>
                  <span><span className={`admin-pill ${STATUS_CLASS_MAP[row.status || ''] || ''}`}>{formatStatusLabel(row.status)}</span></span>
                  <span><small>{formatDateTime(row.sentAt)}</small></span>
                  <span><small>{formatDateTime(row.viewedAt)}</small></span>
                  <span><small>{formatDateTime(row.acceptedAt)}</small></span>
                  <span><small>{formatDateTime(row.expiredAt)}</small></span>
                  <span><small>{formatDateTime(row.cancelledAt)}</small></span>
                  <span><small>{formatDateTime(row.completedAt)}</small></span>
                </div>
              ))}
              {!detail.bookingRows.length ? <p className="admin-empty">No underlying booking rows were returned for this trip.</p> : null}
            </div>
          </section>
        </>
      ) : (
        <div className="admin-panel">
          <p className="admin-empty">The trip detail is not available.</p>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminTripDetailPage;
